import { useRef, useState } from 'react'
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ApiError, enrollRecoveryCodes, registerPasskey, supportsPasskeys } from '../lib/api'
import { WebAuthnUserError } from '../lib/webauthnCeremony'
import { useSession } from '../lib/useSession'

export function RegisterPasskeyPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { isAuthenticated, amr } = useSession()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [codes, setCodes] = useState<string[] | null>(null)
  const [done, setDone] = useState(false)
  const inFlight = useRef(false)
  const fromRecovery = Boolean((location.state as { fromRecovery?: boolean } | null)?.fromRecovery)

  if (!isAuthenticated) {
    return <Navigate to="/passkey" replace />
  }

  async function onRegister() {
    if (inFlight.current) return
    inFlight.current = true
    setError(null)
    if (!supportsPasskeys()) {
      setError(t('auth.webauthnUnsupported'))
      inFlight.current = false
      return
    }
    setBusy(true)
    try {
      await registerPasskey(fromRecovery ? 'Recovered device' : 'Primary')
      setDone(true)
    } catch (err) {
      if (err instanceof WebAuthnUserError) {
        if (err.code === 'cancelled') setError(t('auth.passkeyCancelled'))
        else if (err.code === 'already_registered') setError(t('auth.passkeyAlreadyRegistered'))
        else if (err.code === 'pending') setError(t('auth.passkeyPending'))
        else setError(err.message)
      } else if (err instanceof ApiError) {
        setError(err.message)
      } else {
        setError(t('auth.registerFailed'))
      }
    } finally {
      setBusy(false)
      inFlight.current = false
    }
  }

  async function onEnrollCodes() {
    setError(null)
    setBusy(true)
    try {
      const res = await enrollRecoveryCodes()
      setCodes(res.codes)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('auth.codesFailed'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="welcome card">
      <h1>{t('auth.registerTitle')}</h1>
      <p className="lede">
        {fromRecovery || amr.includes('recovery') || amr.includes('otp')
          ? t('auth.registerAfterRecovery')
          : t('auth.registerLede')}
      </p>

      {!done ? (
        <button type="button" className="btn btn-primary" disabled={busy} onClick={() => void onRegister()}>
          {busy ? t('auth.working') : t('auth.registerPasskey')}
        </button>
      ) : (
        <div className="auth-form">
          <p className="ok">{t('auth.registerOk')}</p>
          <button type="button" className="btn btn-primary" onClick={() => navigate('/inbox', { replace: true })}>
            {t('auth.gotoInbox')}
          </button>
          {!codes ? (
            <button type="button" className="btn" disabled={busy} onClick={() => void onEnrollCodes()}>
              {t('auth.showRecoveryCodes')}
            </button>
          ) : (
            <div className="codes-box">
              <p className="warn">{t('auth.codesOnce')}</p>
              <ul className="codes-list">
                {codes.map((c) => (
                  <li key={c}>
                    <code>{c}</code>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {error ? (
        <p className="warn" role="alert">
          {error}
        </p>
      ) : null}

      <p className="muted">
        <Link to="/sessions">{t('auth.sessions')}</Link>
        {' · '}
        <Link to="/accounts">{t('auth.accounts')}</Link>
      </p>
    </section>
  )
}
